import styled from "@emotion/styled";
import Flatten from "@flatten-js/core";
import { useCallback, useState } from "react";

import { useDimensions, useZoom } from "../perspective";
import { useTouchPosition } from "../touch";
import { Arrow, Connector, Dot } from "./Connector";
import { defaultCurveConfig } from "./curve";
import { SvgCanvas } from "./styled/SvgCanvas";
import type { CurveConfig, DrawingCurveDescriptor, DrawingState, SceneBoundsMap } from "./types";
import { useDrawing } from "./useDrawing";
import { useMousePosition } from "./useMousePosition";

const OuterPath = styled.path`
  fill: transparent;
  stroke: ${({ theme }) => theme.moduleMap.background};
  stroke-width: 4px;
`;

const Path = styled.path<{ color: string }>`
  fill: transparent;
  stroke: ${({ color }) => color};
  stroke-width: 2px;
`;

const OuterArrow = styled(Arrow)`
  fill: ${({ theme }) => theme.moduleMap.background};
  transform: translate3d(0, -1px, 0) scale(1.3);
`;

type Props = {
  drawingState: DrawingState;
  sceneBounds: SceneBoundsMap;
  color: string;
  options?: CurveConfig;
};

const getDrawingSvgPathD = ({ startOffset, startCurve, controlPointA, endOffset }: DrawingCurveDescriptor) => {
  return `
      M ${startOffset.x},${startOffset.y}
      L ${startCurve.x},${startCurve.y}
      C
        ${startCurve.x},${startCurve.y}
        ${controlPointA.x},${controlPointA.y}
        ${endOffset.x},${endOffset.y}
    `;
};

export const Drawing = ({ drawingState, sceneBounds, color, options = defaultCurveConfig }: Props) => {
  const { usableWidth, usableHeight, positionOrigin } = useDimensions();
  const { zoom } = useZoom();
  const [svgCanvasRef, setSvgCanvasRef] = useState<SVGSVGElement>();
  const refCallback = useCallback((node: SVGSVGElement) => setSvgCanvasRef(node), []);

  const mousePosition = useMousePosition(svgCanvasRef, zoom, { x: positionOrigin.x * -1, y: positionOrigin.y * -1 });
  const touchPosition = useTouchPosition(svgCanvasRef, zoom, { x: positionOrigin.x * -1, y: positionOrigin.y * -1 });

  const { curveDescriptor, connector } = useDrawing(drawingState, sceneBounds, mousePosition ?? touchPosition);

  /*
    We may want to add the Arrow to the Drawing later on,
    so the computation of its slope is left here
  */

  const drawingSlope = !curveDescriptor
    ? undefined
    : (new Flatten.Vector(curveDescriptor.startOffset, curveDescriptor.endOffset).slope * 180) / Math.PI - 90;

  return (
    <SvgCanvas
      ref={refCallback}
      width={usableWidth}
      height={usableHeight}
      positionOrigin={positionOrigin}
      style={{ zIndex: 3 }}
    >
      {curveDescriptor && (
        <g data-test="moving-connector-container">
          <g
            transform={`
              translate(${curveDescriptor.endOffset.x}, ${curveDescriptor.endOffset.y}) 
              rotate(${drawingSlope ?? 0} 0 0) 
              translate(0, -${options.arrowSize * 0.8}) 
            `}
          >
            <OuterArrow size={options.arrowSize} state="moving" />
          </g>
          <OuterPath d={getDrawingSvgPathD(curveDescriptor)} />
          <Path d={getDrawingSvgPathD(curveDescriptor)} color={color} />
          <g transform={`translate(${curveDescriptor.startOffset.x}, ${curveDescriptor.startOffset.y})`}>
            <Dot r={options.dotSize} color={color} />
          </g>
          <g
            transform={`
              translate(${curveDescriptor.endOffset.x}, ${curveDescriptor.endOffset.y}) 
              rotate(${drawingSlope ?? 0} 0 0) 
              translate(0, -${options.arrowSize * 0.8}) 
            `}
          >
            <Arrow size={options.arrowSize} color={color} state="moving" />
          </g>
        </g>
      )}
      {connector && <Connector connector={connector} />}
    </SvgCanvas>
  );
};
